import AgoraRTC from "agora-rtc-sdk-ng";

class AgoraService {
  constructor() {
    this.client = null;
    this.localAudioTrack = null;
    this.localVideoTrack = null;
    this.remoteUsers = {};
    this.isJoined = false;
  }

  /**
   * Initialize Agora Client
   * @param {string} appId - Agora App ID
   * @param {string} mode - 'rtc' or 'live'
   * @param {string} codec - 'vp8' or 'h264'
   */
  init(appId, mode = 'rtc', codec = 'vp8') {
    this.client = AgoraRTC.createClient({ mode, codec });
    
    // Event listeners
    this.client.on("user-published", this.handleUserPublished.bind(this));
    this.client.on("user-unpublished", this.handleUserUnpublished.bind(this));
    this.client.on("user-joined", this.handleUserJoined.bind(this));
    this.client.on("user-left", this.handleUserLeft.bind(this));
    
    console.log("✅ Agora Client initialized");
  }

  /**
   * Join a video call channel
   * @param {string} channel - Channel name
   * @param {string} token - Agora token (or null for testing)
   * @param {string|number} uid - User ID
   */
  async join(channel, token, uid) {
    try {
      if (!this.client) {
        throw new Error("Ứng dụng khách Agora chưa được khởi tạo");
      }

      // Join the channel
      await this.client.join(
        process.env.REACT_APP_AGORA_APP_ID,
        channel,
        token,
        uid
      );

      this.isJoined = true;
      console.log("✅ Joined channel:", channel);
      
      return uid;
    } catch (error) {
      console.error("❌ Error joining channel:", error);
      throw error;
    }
  }

  /**
   * Create and publish local audio and video tracks
   * @param {boolean} video - Enable video
   * @param {boolean} audio - Enable audio
   */
  async createAndPublishTracks(video = true, audio = true) {
    try {
      if (audio) {
        this.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
      }
      
      if (video) {
        this.localVideoTrack = await AgoraRTC.createCameraVideoTrack();
      }

      // Publish tracks to the channel
      const tracks = [];
      if (this.localAudioTrack) tracks.push(this.localAudioTrack);
      if (this.localVideoTrack) tracks.push(this.localVideoTrack);

      if (tracks.length > 0) {
        await this.client.publish(tracks);
        console.log("✅ Published local tracks");
      }

      return {
        audioTrack: this.localAudioTrack,
        videoTrack: this.localVideoTrack,
      };
    } catch (error) {
      console.error("❌ Error creating/publishing tracks:", error);
      throw error;
    }
  }

  /**
   * Toggle local video
   */
  async toggleVideo(enabled) {
    if (this.localVideoTrack) {
      await this.localVideoTrack.setEnabled(enabled);
      return enabled;
    }
    return false;
  }

  /**
   * Toggle local audio
   */
  async toggleAudio(enabled) {
    if (this.localAudioTrack) {
      await this.localAudioTrack.setEnabled(enabled);
      return enabled;
    }
    return false;
  }

  /**
   * Leave the channel and clean up
   */
  async leave() {
    try {
      // Close local tracks
      if (this.localAudioTrack) {
        this.localAudioTrack.close();
        this.localAudioTrack = null;
      }
      
      if (this.localVideoTrack) {
        this.localVideoTrack.close();
        this.localVideoTrack = null;
      }

      // Leave the channel
      if (this.client && this.isJoined) {
        await this.client.leave();
        this.isJoined = false;
      }

      this.remoteUsers = {};
      console.log("✅ Left channel successfully");
    } catch (error) {
      console.error("❌ Error leaving channel:", error);
      throw error;
    }
  }

  /**
   * Handle user published event
   */
  async handleUserPublished(user, mediaType) {
    try {
      await this.client.subscribe(user, mediaType);
      console.log("✅ Subscribed to user:", user.uid, mediaType);

      if (mediaType === "video") {
        this.remoteUsers[user.uid] = user;
      }

      if (mediaType === "audio") {
        user.audioTrack?.play();
      }

      // Trigger callback if exists
      if (this.onUserPublished) {
        this.onUserPublished(user, mediaType);
      }
    } catch (error) {
      console.error("❌ Error subscribing to user:", error);
    }
  }

  /**
   * Handle user unpublished event
   */
  handleUserUnpublished(user, mediaType) {
    console.log("⚠️ User unpublished:", user.uid, mediaType);
    
    if (mediaType === "video") {
      delete this.remoteUsers[user.uid];
    }

    // Trigger callback if exists
    if (this.onUserUnpublished) {
      this.onUserUnpublished(user, mediaType);
    }
  }

  /**
   * Handle user joined event
   */
  handleUserJoined(user) {
    console.log("👤 User joined:", user.uid);
    
    if (this.onUserJoined) {
      this.onUserJoined(user);
    }
  }

  /**
   * Handle user left event
   */
  handleUserLeft(user, reason) {
    console.log("👋 User left:", user.uid, reason);
    delete this.remoteUsers[user.uid];
    
    if (this.onUserLeft) {
      this.onUserLeft(user, reason);
    }
  }

  /**
   * Get all remote users
   */
  getRemoteUsers() {
    return Object.values(this.remoteUsers);
  }

  /**
   * Get client statistics
   */
  async getStats() {
    if (this.client) {
      return await this.client.getRTCStats();
    }
    return null;
  }
}

// Export singleton instance
const agoraService = new AgoraService();
export default agoraService;